import { memo, useEffect, useRef, useState } from "react";
import { Button, Flex, Input, InputRef, Pagination, Popconfirm, Space, Table, TableColumnType, TableProps, Tag } from "antd";
import { useTranslation } from "react-i18next";
import { PrinterOutlined, QuestionCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { mdlActionTemplate } from "../../models/domain/ActionTemplate";
import { MailIcon, PdfIcon } from "../common/CustomIcons";
import { FilterDropdownProps } from "antd/es/table/interface";
import Highlighter from 'react-highlight-words';
import { mdlTablePagination } from "../../models/ui-models/TablePagination";


export type ActionTemplateTableProps = {
    dataSource?: mdlActionTemplate[];
    onDelete?: Function;
    cancelActive?: boolean;
    onEdit?: Function;
    onExport?: Function;
    onPrint?: Function;
    onSendMail?: Function;
    expandRowRenderer?: Function;
}

export const ActionTemplateTable = memo(function ActionTemplateTable(props: ActionTemplateTableProps) {
    const { t } = useTranslation();
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);
    useEffect(() => {
    }, [props.dataSource]);

    type DataIndex = keyof mdlActionTemplate;


    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<mdlActionTemplate> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        {t("common.search")}
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        {t("common.reset")}
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        {t("common.filter")}
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        {t("common.close")}
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            record[dataIndex]!
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });



    const columns: TableProps<mdlActionTemplate>['columns'] = [
        {
            title: t("action.template.spoNo"),
            dataIndex: 'spoNo',
            key: 'spoNo',
            align: "center",
            ...getColumnSearchProps('spoNo'),
        },
        {
            title: t("action.template.type"),
            align: "center",
            hidden: !props.cancelActive,
            key: 'typeText',
            dataIndex: 'typeText',
        },
        {
            title: t("action.template.mailSubject"),
            key: 'mailSubject',
            dataIndex: 'mailSubject',
            align: "center",
            ...getColumnSearchProps('mailSubject'),
        },
        {
            title: t("action.template.statusText"),
            key: 'statusText',
            align: "center",
            dataIndex: 'statusText',
            ...getColumnSearchProps('statusText'),
        },
        {
            title: t("common.actions"),
            key: 'action',
            align: 'right',
            render: (_, record: mdlActionTemplate) => (
                <Space size="middle">
                    {
                        props.onEdit
                        &&
                        <Button type="primary" className="btn-success" onClick={() => props.onEdit ? props.onEdit(record.id) : undefined}>
                            {t("common.edit")}
                        </Button>
                    }

                    <Button type="primary" onClick={() => props.onSendMail ? props.onSendMail(record.id) : undefined} icon={<MailIcon />} size="middle">
                        {t("common.email")}
                    </Button>
                    <Button type="primary" onClick={() => props.onExport ? props.onExport(record.id) : undefined} icon={<PdfIcon />} size="middle">
                        {t("common.exportToPDF")}
                    </Button>
                    <Button
                        onClick={() => props.onPrint ? props.onPrint(record.id) : undefined}
                        type="primary" icon={<PrinterOutlined />} size="middle">
                        {t("common.print")}
                    </Button>
                    {
                        !props.cancelActive
                            ?
                            < Popconfirm
                                title={t("common.deleteWarning")}
                                description={t("common.deleteDescription")}
                                okText={t("common.okConfirm")}
                                cancelText={t("common.cancelConfirm")}
                                icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                                onConfirm={() => props.onDelete ? props.onDelete(record.id) : undefined}
                            >
                                <Button type="primary" danger>
                                    {t("common.delete")}
                                </Button>
                            </Popconfirm>
                            :
                            !record.isCancel ?
                                <Popconfirm
                                    title={t("action.history.cancelWarning")}
                                    description={t("action.history.cancelDescription")}
                                    okText={t("common.okConfirm")}
                                    cancelText={t("action.history.cancel")}
                                    icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                                    onConfirm={() => props.onDelete ? props.onDelete(record.id) : undefined}
                                >
                                    <Button type="primary" danger>
                                        {t("action.history.cancel")}
                                    </Button>
                                </Popconfirm>
                                :
                                <Button type="primary" className="btn-success" onClick={() => props.onDelete ? props.onDelete(record.id) : undefined}>
                                    {t("common.new")}
                                </Button>
                    }


                </Space >
            ),
        },
    ];


    return (
        <Table<mdlActionTemplate>
            expandable={{
                expandedRowRender: (record) => (props.expandRowRenderer && props.expandRowRenderer(record.id))
            }}
            pagination={false}
            size="middle"
            columns={columns}
            // scroll={{ x: 'max-content' }}
            dataSource={props.dataSource ? props.dataSource.map((actionTemplate: mdlActionTemplate, index: number) => ({ ...actionTemplate, key: index })) : []}
        />
    )
})